import { CurrencyPipe } from '@angular/common'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { catchError, map, retry } from 'rxjs/operators'
import { CustomPaginationType } from '../../features/shared/custom-components/custom-pagination/modals/enum/custom-pagination.enum'
import { BaseCountry } from '../../models/enum/country.enum'
import { SharedKey, SharedValue } from '../../models/enum/shared.enum'
import { ISharedPagination } from '../../models/interface/shared.interface'
import { RebateClaim, RebateClaimsResults } from '../../models/rebate-claim.model'
import { AppConfigurationService } from '../app-configuration.service'
import { DocumentService } from '../document.service'
import { SharedService } from '../shared/shared.service'

@Injectable({
  providedIn: 'root'
})
export class RebateClaimService {
  constructor(
    private appConfigurationService: AppConfigurationService,
    private http: HttpClient,
    private sharedService: SharedService,
    public documentService: DocumentService,
    public currencyPipe: CurrencyPipe
  ) {}

  public getRebateClaimForm(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/rebateClaimDownload/csv`
    return this.http
      .get(`${url}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        retry(1),
        catchError(() => {
          throw new Error(SharedValue.errorTryAgain)
        })
      )
  }

  public uploadRebateClaimForm(file: any, month: string): Observable<any> {
    const formData = new FormData()
    formData.append('file', file[0])
    formData.append('month', month)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/rebateClaimUpload/csv`
    return this.http.post(url, formData).pipe(catchError(this.sharedService.handleError))
  }

  public getRebateHistory(params?: ISharedPagination): Observable<RebateClaimsResults> {
    let payload: HttpParams = new HttpParams().set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, (params.pageNumber - 1).toString())
    }
    if (params?.sort) {
      payload = payload.set(SharedKey.sort, params.sort)
    }
    if (params?.pageSize) {
      payload = payload.set(SharedKey.pageSize, params.pageSize.toString())
    }
    if (params?.status) {
      payload = payload.set(SharedKey.states, params.status)
    }
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/rebateClaims`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getRebateClaimByCode(rebateClaimCode: string): Observable<RebateClaim> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/rebateClaims/${rebateClaimCode}`
    return this.http.get<any>(url).pipe(
      map((res) => {
        const newResponse = { ...res }
        newResponse.items = res?.items.map((item) => {
          const newItem = { ...item }
          newItem.distributorNo = newResponse.distributorNo
          return newItem
        })
        return newResponse
      }),
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public downloadCreditNoteByNumber(documentId: string): Observable<any> {
    return this.documentService.getDocumentByNumber(documentId).pipe(
      map((res) => {
        res = this.mapCreditNoteFields(null, res, ' CR')
        if (!res?.items?.length) {
          throw new Error(SharedValue.invalidDocument)
        }
        return res
      })
    )
  }

  public downloadCreditNote(rebateClaimCode: string, documentId: string): Observable<any> {
    return this.documentService.getDocumentByOrder(documentId).pipe(
      map((res) => {
        res = this.mapCreditNoteFields(rebateClaimCode, res, ' CR')
        if (!res?.items?.length) {
          throw new Error(SharedValue.invalidDocument)
        }
        return res
      })
    )
  }

  private mapCreditNoteFields(rebateClaimCode: string, orderDocument: any, type: string): any {
    if (!orderDocument) {
      return null
    }
    return {
      partnerShipToAddress: orderDocument.partnerShipToAddress,
      partnerBillToAddress: orderDocument.partnerbBillToAddress,
      invoiceDocumentNumber: orderDocument.documentNumber,
      invoiceDocumentDate: orderDocument.invoiceDate,
      payerNumber: orderDocument.payerNumber,
      billingReference: orderDocument.billingReference,
      deliveryDocumentNumber: orderDocument.deliveryNumber,
      deliveryDate: orderDocument.deliveryDate,
      salesOrder: orderDocument.orderNumber,
      customerOrderNumber: rebateClaimCode || orderDocument.customerOrderNumber,
      paymentTerms: orderDocument.termsOfPayment,
      incoTerms: orderDocument.incoTerms1,
      discountAvailable: this.formatCurrency(+orderDocument.finalDiscount),
      gstTotal: this.formatCurrency(orderDocument.invoiceItemsData?.overAllTax?.value) + type || 0.0,
      currency: orderDocument.currency?.isocode,
      total: this.formatCurrency(orderDocument.invoiceItemsData?.grandTotal?.value) + type || 0.0,
      subTotal: this.formatCurrency(orderDocument.invoiceItemsData?.netValue?.value) + type || 0.0,
      items: orderDocument.invoiceItemsData?.entries?.map((invoiceItemData) => {
        const item: any = {
          itemNumber: invoiceItemData.posNo || '',
          marketCode: invoiceItemData.marketCode || '',
          materialNumber: invoiceItemData.product?.code || '',
          description: invoiceItemData.product?.name || '',
          quantity: invoiceItemData.quantity || '',
          uom: invoiceItemData.unit || '',
          price: this.formatCurrency(invoiceItemData.grossPrice?.value) || 0.0,
          taxRate: this.formatCurrency(invoiceItemData.gst) || 0.0,
          itemValue: this.formatCurrency(invoiceItemData.netPrice?.value) + ' ' + type || 0.0
        }
        return item
      })
    }
  }

  public generateCreditPDF(orderDocument: any): void {
    this.generateCreditDocument(orderDocument, 'Credit Note for Price Support')
  }

  private generateCreditDocument(orderDocument, title: string): void {
    const docService = this.documentService
    const { customerService, abnOrGstLabel, abnOrGstValue, companyName, paymentTerms } = this.getCountryBasedInfo(
      docService,
      this.sharedService.baseSiteId
    )

    const documentDefinition = {
      footer: function (currentPage, pageCount) {
        return [
          { text: '\nPage                     ' + currentPage.toString() + ' of ' + pageCount, alignment: 'center' }
        ]
      },
      content: [
        {
          columns: [
            {
              image: docService.getAsaleoLogo(),
              alignment: 'left',
              width: 60
            },
            {
              text: '',
              width: '*'
            },
            {
              text: [
                { text: 'Customer Service\n', fontSize: 5, alignment: 'left' },
                {
                  text: customerService,
                  fontSize: 5,
                  alignment: 'left'
                },
                { text: 'Credit Note', fontSize: 18, alignment: 'center' }
              ],
              alignment: 'center',
              width: 150
            }
          ]
        },
        { text: '\n' },
        {
          columnGap: 10,
          columns: [
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: [
                        { text: 'Bill To', style: 'defaultFontBold' },
                        { text: ' ', style: 'defaultFont' },
                        { text: orderDocument.partnerBillToAddress?.name, style: 'defaultFont' },
                        {
                          style: 'defaultFont',
                          text:
                            orderDocument.partnerBillToAddress?.streetHouseNumber1 +
                            ' - ' +
                            orderDocument.partnerBillToAddress?.city
                        },
                        { style: 'defaultFont', text: 'PO BOX ' + orderDocument.partnerBillToAddress?.poBox || '' },
                        {
                          style: 'defaultFont',
                          text:
                            orderDocument.partnerBillToAddress?.suburb +
                            ' ' +
                            orderDocument.partnerBillToAddress?.poBoxPostalCode
                        },
                        { style: 'defaultFont', text: orderDocument.partnerBillToAddress?.countryCode || '' }
                      ]
                    }
                  ]
                ]
              }
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: [
                        { text: 'Ship To', style: 'defaultFontBold' },
                        { text: ' ', style: 'defaultFont' },
                        { text: orderDocument.partnerShipToAddress?.name, style: 'defaultFont' },
                        {
                          style: 'defaultFont',
                          text:
                            orderDocument.partnerShipToAddress?.streetHouseNumber1 +
                            ' - ' +
                            orderDocument.partnerShipToAddress?.city
                        },
                        { style: 'defaultFont', text: 'PO BOX ' + orderDocument.partnerShipToAddress?.poBox || '' },
                        {
                          style: 'defaultFont',
                          text:
                            orderDocument.partnerShipToAddress?.suburb +
                            ' ' +
                            orderDocument.partnerShipToAddress?.postalCode
                        },
                        { style: 'defaultFont', text: orderDocument.partnerShipToAddress?.countryCode || '' }
                      ]
                    }
                  ]
                ]
              }
            },
            {
              width: '*',
              columns: [
                {
                  stack: [
                    { text: 'Credit Doc. Number', style: 'defaultFontBold', lineHeight: 1.2 },
                    { text: 'Credit Doc. Date', style: 'defaultFontBold', lineHeight: 1.2 },
                    { text: 'Billing Reference', style: 'defaultFontBold', lineHeight: 1.2 },
                    { text: 'Payer Number', style: 'defaultFontBold', lineHeight: 1.2 }
                  ]
                },
                {
                  stack: [
                    { text: orderDocument.invoiceDocumentNumber, style: 'defaultFont', lineHeight: 1.2 },
                    { text: orderDocument.invoiceDocumentDate, style: 'defaultFont', lineHeight: 1.2 },
                    { text: orderDocument.billingReference, style: 'defaultFont', lineHeight: 1.2 },
                    { text: orderDocument.payerNumber, style: 'defaultFont', lineHeight: 1.2 }
                  ]
                }
              ]
            }
          ]
        },
        { text: '\n\n' },
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  layout: 'noBorders',
                  table: {
                    headerRows: 1,
                    widths: [40, 40, 40, '*', 40, 40, 40, 40, 40],
                    body: [
                      [
                        { style: 'defaultFontBold', text: 'Item' },
                        { style: 'defaultFontBold', text: 'Market code' },
                        { style: 'defaultFontBold', text: 'Material' },
                        { style: 'defaultFontBold', text: 'Description' },
                        { style: 'defaultFontBold', text: 'Quantity' },
                        { style: 'defaultFontBold', text: 'UOM' },
                        { style: 'defaultFontBold', text: 'Price' },
                        { style: 'defaultFontBold', text: 'GST%' },
                        { style: 'defaultFontBold', text: 'Value' }
                      ]
                    ]
                  }
                }
              ],
              [
                {
                  layout: 'noBorders',
                  table: {
                    widths: ['*'],
                    body: [
                      [
                        {
                          columns: [
                            {
                              text: ' ',
                              width: 30
                            },
                            {
                              text: 'Sales Order: ' + orderDocument.salesOrder,
                              style: 'defaultFontBold',
                              width: 150
                            },
                            {
                              text: 'Cust.Order Number: ' + orderDocument.customerOrderNumber + '\n',
                              style: 'defaultFontBold',
                              width: 150
                            }
                          ]
                        }
                      ],
                      [
                        {
                          layout: 'noBorders',
                          table: {
                            widths: [40, 40, 40, '*', 40, 40, 40, 40, 40],
                            body: []
                          }
                        }
                      ],
                      [{ text: '\n' }],
                      [
                        {
                          stack: [
                            {
                              columns: [
                                {
                                  text: 'Subtotal ' + orderDocument.subTotal,
                                  style: 'defaultFontBold',
                                  alignment: 'right'
                                },
                                {
                                  text: ' ',
                                  style: 'defaultFontBold',
                                  width: 25
                                }
                              ]
                            },
                            {
                              text: paymentTerms,
                              style: 'small'
                            }
                          ]
                        }
                      ],
                      [
                        {
                          alignment: 'right',
                          style: 'defaultFontBold',
                          columns: [
                            {
                              text: '',
                              width: '*'
                            },
                            {
                              text: 'GST\nTotal',
                              width: 50
                            },
                            {
                              text: '\n' + orderDocument.currency,
                              width: 50
                            },
                            {
                              text: orderDocument.gstTotal + '\n' + orderDocument.total,
                              width: 50
                            },
                            {
                              text: ' ',
                              width: 25
                            }
                          ]
                        }
                      ]
                    ]
                  }
                }
              ]
            ]
          }
        },
        {
          columns: [
            {
              stack: [
                {
                  text: [
                    { text: `\n${abnOrGstLabel}: `, style: 'defaultFontBold' },
                    { text: abnOrGstValue, style: 'defaultFontBold' }
                  ]
                },
                {
                  text: companyName,
                  style: 'defaultFont'
                }
              ]
            },
            { text: ' ' }
          ]
        },
        {
          text: docService.footerInfo,
          style: 'defaultFontBold'
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true
        },
        table: {
          fontSize: 7,
          bold: true
        },
        tableContent: {
          fontSize: 6,
          bold: false
        },
        bigger: {
          fontSize: 15,
          italics: true
        },
        small: {
          fontSize: 5
        },
        SmallBold: {
          fontSize: 6,
          bold: true
        },
        defaultFont: {
          fontSize: 6
        },
        defaultFontBold: {
          fontSize: 6,
          bold: true
        }
      },
      defaultStyle: {
        columnGap: 0
      },
      pageMargins: [40, 40, 40, 48.5]
    }

    const innerTable: any = documentDefinition.content[4].table.body[1][0]
    const entries: any = innerTable.table.body[1][0]
    orderDocument.items?.forEach((item) => {
      const creditNoteItem: any = [
        { style: 'defaultFont', text: item.itemNumber },
        { style: 'defaultFont', text: item.marketCode },
        { style: 'defaultFont', text: item.materialNumber },
        { style: 'defaultFont', text: item.description },
        { style: 'defaultFont', text: item.quantity?.toString() },
        { style: 'defaultFont', text: item.uom },
        { style: 'defaultFont', text: item.price?.toString() },
        { style: 'defaultFont', text: item.taxRate?.toString() },
        { style: 'defaultFont', text: item.itemValue?.toString() }
      ]
      entries.table.body.push(creditNoteItem)
    })
    docService.createPDF(documentDefinition, title)
  }

  public downloadDebitNoteByNumber(documentId: string): Observable<any> {
    return this.documentService.getDocumentByNumber(documentId).pipe(
      map((res) => {
        res = this.mapDebitNoteFieldsForTicket(null, res, '')
        if (!res?.debitItems?.length) {
          throw new Error(SharedValue.invalidDocument)
        }
        return res
      })
    )
  }

  public downloadDebitNote(rebateClaimCode: string, documentId: string): Observable<any> {
    return this.documentService.getDocumentByOrder(documentId).pipe(
      map((res) => {
        res = this.mapDebitNoteFields(rebateClaimCode, res, '')
        if (!res?.debitItems?.length) {
          throw new Error(SharedValue.invalidDocument)
        }
        return res
      })
    )
  }

  private mapDebitNoteFields(rebateClaimCode: string, orderDocument: any, type: string): any {
    if (!orderDocument) {
      return null
    }
    return {
      debitSoldTo: orderDocument.partnerShipToAddress,
      yourReference: rebateClaimCode || ' ',
      debitNumber: orderDocument.documentNumber || ' ',
      debitDate: orderDocument.orderDate || ' ',
      debitPayerNumber: orderDocument.b2bUnit || ' ',
      debitCurrency: orderDocument.currency?.isocode,
      debitGstTotal: this.formatCurrency(orderDocument.invoiceItemsData?.overAllTax?.value) || 0.0 + type,
      debitSubTotal: this.formatCurrency(orderDocument.invoiceItemsData?.netValue?.value) || 0.0 + type,
      debitTotal: this.formatCurrency(orderDocument.invoiceItemsData?.grandTotal?.value) || 0.0 + type,
      debitItems: orderDocument.invoiceItemsData?.entries?.map((invoiceItemData) => {
        const item: any = {
          customer: invoiceItemData.customerName || ' ',
          distributorReference: rebateClaimCode || ' ',
          distributorSaleDate: invoiceItemData.distributorSaleDate || ' ',
          materialNumber: invoiceItemData.product?.code || ' ',
          materialDescription: invoiceItemData.product?.name || ' ',
          quantity: invoiceItemData.quantity || ' ',
          taxRate: this.formatCurrency(invoiceItemData.gst) || 0.0,
          revisedPrice: this.formatCurrency(invoiceItemData.netPrice?.value) + type || 0.0
        }
        return item
      })
    }
  }

  private mapDebitNoteFieldsForTicket(rebateClaimCode: string, orderDocument: any, type: string): any {
    if (!orderDocument) {
      return null
    }
    return {
      debitSoldTo: orderDocument.partnerShipToAddress,
      yourReference: orderDocument.poNumber,
      debitNumber: orderDocument.documentNumber || ' ',
      debitDate: orderDocument.orderDate || ' ',
      debitPayerNumber: orderDocument.b2bUnit || ' ',
      debitCurrency: orderDocument.currency?.isocode,
      debitGstTotal: this.formatCurrency(orderDocument.invoiceItemsData?.overAllTax?.value) || 0.0 + type,
      debitSubTotal: this.formatCurrency(orderDocument.invoiceItemsData?.netValue?.value) || 0.0 + type,
      debitTotal: this.formatCurrency(orderDocument.invoiceItemsData?.grandTotal?.value) || 0.0 + type,
      debitItems: orderDocument.invoiceItemsData?.entries?.map((invoiceItemData) => {
        const item: any = {
          customer: invoiceItemData.customerName || ' ',
          distributorReference: invoiceItemData.poNumber,
          distributorSaleDate: invoiceItemData.distributorSaleDate || ' ',
          materialNumber: invoiceItemData.product?.code || ' ',
          materialDescription: invoiceItemData.product?.name || ' ',
          quantity: invoiceItemData.quantity || ' ',
          taxRate: this.formatCurrency(invoiceItemData.gst) || 0.0,
          revisedPrice: this.formatCurrency(invoiceItemData.netPrice?.value) + type || 0.0
        }
        return item
      })
    }
  }

  public generateDebitPDF(orderDocument: any): void {
    this.generateDebitDocument(orderDocument, 'Debit Note for Price Support')
  }

  public generateDebitPDFForClaim(orderDocument: any, documentId: string): void {
    this.generateDebitDocument(orderDocument, 'Debit Note ' + documentId)
  }

  private generateDebitDocument(orderDocument, title: string): void {
    const docService = this.documentService
    const { customerService, abnOrGstLabel, abnOrGstValue, companyName } = this.getCountryBasedInfo(
      docService,
      this.sharedService.baseSiteId
    )

    const documentDefinition = {
      footer: function (currentPage, pageCount) {
        return [
          { text: '\nPage                     ' + currentPage.toString() + ' of ' + pageCount, alignment: 'center' }
        ]
      },
      content: [
        {
          columns: [
            {
              image: docService.getAsaleoLogo(),
              alignment: 'left',
              width: 60
            },
            {
              text: '',
              width: '*'
            },
            {
              text: [
                { text: 'Customer Service\n', fontSize: 5, alignment: 'left' },
                {
                  text: customerService,
                  fontSize: 5,
                  alignment: 'left'
                },
                { text: 'Debit Note', fontSize: 18, alignment: 'center' }
              ],
              alignment: 'center',
              width: 150
            }
          ]
        },
        { text: '\n' },
        {
          columnGap: 10,
          columns: [
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: [
                        {
                          columns: [
                            { text: 'Sold To\n', style: 'defaultFontBold', lineHeight: 1.2 },
                            {
                              style: 'defaultFont',
                              text: orderDocument.debitSoldTo?.partnerID || ' ',
                              lineHeight: 1.2
                            }
                          ]
                        },
                        { text: '\n', style: 'defaultFont' },
                        { text: orderDocument.debitSoldTo?.name, style: 'defaultFont' },
                        {
                          style: 'defaultFont',
                          text:
                            (orderDocument.debitSoldTo?.streetHouseNumber1 || ' ') +
                            ' - ' +
                            orderDocument.debitSoldTo?.city
                        },
                        { style: 'defaultFont', text: 'PO BOX ' + orderDocument.debitSoldTo?.poBox || '' },
                        {
                          style: 'defaultFont',
                          text:
                            (orderDocument.debitSoldTo?.suburb || '') + ' ' + orderDocument.debitSoldTo?.poBoxPostalCode
                        },
                        { style: 'defaultFont', text: orderDocument.debitSoldTo?.countryCode || '' }
                      ]
                    }
                  ]
                ]
              }
            },
            {
              layout: 'noBorders',
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: [
                        { text: '\n', style: 'defaultFont' },
                        { style: 'defaultFont', text: 'Your Reference: ' + orderDocument.yourReference }
                      ]
                    }
                  ]
                ]
              }
            },
            {
              width: '*',
              columns: [
                {
                  stack: [
                    { text: 'Debit Number', style: 'defaultFontBold', lineHeight: 1.2 },
                    { text: 'Debit Date', style: 'defaultFontBold', lineHeight: 1.2 },
                    { text: 'Payer Number', style: 'defaultFontBold', lineHeight: 1.2 }
                  ]
                },
                {
                  stack: [
                    { text: orderDocument.debitNumber, style: 'defaultFont', lineHeight: 1.2 },
                    { text: orderDocument.debitDate, style: 'defaultFont', lineHeight: 1.2 },
                    { text: orderDocument.debitPayerNumber, style: 'defaultFont', lineHeight: 1.2 }
                  ]
                }
              ]
            }
          ]
        },
        { text: '\n\n' },
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  layout: 'noBorders',
                  table: {
                    headerRows: 1,
                    widths: [60, 60, 50, 40, '*', 40, 40, 40],
                    body: [
                      [
                        { style: 'defaultFontBold', text: 'Customer' },
                        { style: 'defaultFontBold', text: "Distributor's Reference" },
                        { style: 'defaultFontBold', text: "Distributor's Sale Date" },
                        { style: 'defaultFontBold', text: 'Material' },
                        { style: 'defaultFontBold', text: 'Description' },
                        { style: 'defaultFontBold', text: 'Quantity' },
                        { style: 'defaultFontBold', text: 'GST%' },
                        { style: 'defaultFontBold', text: 'Revised Price' }
                      ]
                    ]
                  }
                }
              ],
              [
                {
                  layout: 'noBorders',
                  table: {
                    widths: ['*'],
                    body: [
                      [
                        {
                          layout: 'noBorders',
                          table: {
                            widths: [60, 60, 50, 40, '*', 40, 40, 40],
                            body: []
                          }
                        }
                      ],
                      [{ text: '\n' }],
                      [
                        {
                          stack: [
                            {
                              columns: [
                                { text: '', width: '*' },
                                { text: 'Subtotal ', width: 40, style: 'defaultFontBold' },
                                { text: ' ', width: 50 },
                                {
                                  text: orderDocument.debitSubTotal,
                                  style: 'defaultFontBold',
                                  alignment: 'right',
                                  width: 'auto'
                                },
                                { text: ' ', style: 'defaultFontBold', width: 25 }
                              ]
                            }
                          ]
                        }
                      ],
                      [
                        {
                          alignment: 'right',
                          style: 'defaultFontBold',
                          columns: [
                            {
                              text: '',
                              width: '*'
                            },
                            {
                              text: 'GST\nTotal',
                              width: 50
                            },
                            {
                              text: '\n' + orderDocument.debitCurrency,
                              width: 50
                            },
                            {
                              text: orderDocument.debitGstTotal + '\n' + orderDocument.debitTotal,
                              width: 50
                            },
                            {
                              text: ' ',
                              width: 25
                            }
                          ]
                        }
                      ]
                    ]
                  }
                }
              ]
            ]
          }
        },
        {
          columns: [
            {
              stack: [
                {
                  text: [
                    { text: `\n${abnOrGstLabel}: `, style: 'defaultFontBold' },
                    { text: abnOrGstValue, style: 'defaultFontBold' }
                  ]
                },
                {
                  text: companyName,
                  style: 'defaultFont'
                }
              ]
            },
            {
              text: docService.creditNoteInfo,
              style: 'defaultFont',
              alignment: 'left'
            },
            { text: ' ' }
          ]
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true
        },
        table: {
          fontSize: 7,
          bold: true
        },
        tableContent: {
          fontSize: 6,
          bold: false
        },
        bigger: {
          fontSize: 15,
          italics: true
        },
        small: {
          fontSize: 5
        },
        SmallBold: {
          fontSize: 6,
          bold: true
        },
        defaultFont: {
          fontSize: 6
        },
        defaultFontBold: {
          fontSize: 6,
          bold: true
        }
      },
      defaultStyle: {
        columnGap: 0
      }
    }

    const innerTable: any = documentDefinition.content[4].table.body[1][0]
    const entries: any = innerTable.table.body[0][0]
    orderDocument.debitItems?.forEach((item) => {
      const creditNoteItem: any = [
        { style: 'defaultFont', text: item.customer },
        { style: 'defaultFont', text: item.distributorReference },
        { style: 'defaultFont', text: item.distributorSaleDate },
        { style: 'defaultFont', text: item.materialNumber },
        { style: 'defaultFont', text: item.materialDescription },
        { style: 'defaultFont', text: item.quantity },
        { style: 'defaultFont', text: item.taxRate?.toString() },
        { style: 'defaultFont', text: item.revisedPrice }
      ]
      entries.table.body.push(creditNoteItem)
    })
    docService.createPDF(documentDefinition, title)
  }

  private getCountryBasedInfo(docService, baseSiteId): any {
    let customerService = docService.customerService
    let abnOrGstLabel = 'ABN'
    let abnOrGstValue = docService.abnNumber
    const companyName = docService.companyName
    const paymentTerms = docService.paymentTerms

    if (baseSiteId == BaseCountry.NZ) {
      customerService = docService.customerService_NZ
      abnOrGstLabel = 'GST Reg number'
      abnOrGstValue = docService.gstNumber
    }

    return { customerService, abnOrGstLabel, abnOrGstValue, companyName, paymentTerms }
  }

  private formatCurrency(value): string {
    if (!value) {
      return ''
    }
    return this.currencyPipe.transform(value)
  }
}
